import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import short from 'short-uuid';

import { AppContext, AppContextType } from '@efficiently/models';

import { APP_CONTEXT_ROUTES } from '@efficiently/client-api-app-context';
import { PermissionsService } from '@efficiently/client-api-permissions';
import { ClientLoggerFacadeService } from '@efficiently/client-core-logger';
import { isValidShortUuid } from '@efficiently/client-util-helpers';

import { AppContextStoreService } from './app-context-store.service';

type RouterLifecycleEvent = NavigationStart | NavigationEnd | NavigationCancel | NavigationError;

/**
 * Characters that terminate the short project id inside a Supply URL: the next
 * path segment, the query string, the fragment, an auxiliary outlet, or a matrix
 * parameter. `;` is in the set because the router reads `<id>;v=1` as the id
 * `<id>` and activates that project, so the context must name it too.
 */
const SUPPLY_PROJECT_ID_TERMINATORS = /[/?#(;]/;

/** Raised when a URL under the Supply project prefix derives no project and falls back to Global. */
const PROJECT_UNRESOLVED_EVENT = 'app_context.project_unresolved';

const PROJECT_UNRESOLVED_TEXT = 'A Supply URL derived no project context';

/** Starts the part of a URL that is not the route: the query string or the fragment. */
const ROUTE_TAIL_TERMINATORS = /[?#]/;

/**
 * A matrix parameter group on one path segment — `;v=1` in `/supply/p/<id>;v=1`.
 *
 * `(` and `)` are excluded from the run because an auxiliary outlet opens one
 * immediately after a matrix parameter: without them, `;v=1(comments:chats/all)`
 * matches as far as the next literal `/`, which is INSIDE the outlet path, and
 * the stripped result is a route that was never navigated to.
 */
const MATRIX_PARAMETERS = /;[^/()]*/g;

/**
 * The route of a URL: the path with its query string, fragment and matrix
 * parameters removed.
 *
 * A record reaches the SIEM from every signed-in session, so the query string and
 * the matrix parameters are dropped: both are user input by definition.
 *
 * **The project-id segment is kept verbatim, and that is deliberate** — it is the
 * whole diagnostic value of the record, since this event only fires when that
 * segment failed `isValidShortUuid`. It is therefore unvalidated external input
 * and must be read as such: it is a path segment, never an identifier, and
 * nothing downstream may treat it as one.
 */
function toRoute(url: string): string {
	const [path] = url.split(ROUTE_TAIL_TERMINATORS);

	return path.replace(MATRIX_PARAMETERS, '');
}

/**
 * Listens to router events and updates app context accordingly.
 *
 * Two-layer store:
 * - `navigationAppContext` — set on `NavigationStart`, takes precedence in
 *   `getAppContext()`. Held for the entire duration of the navigation so that
 *   guards, resolvers AND freshly-activated components (constructed between
 *   `ResolveEnd` and `NavigationEnd`) all observe the new target context. This
 *   prevents in-flight GraphQL requests issued during component init from
 *   leaking the previous page's context header.
 * - `currentAppContext` — written on `NavigationEnd`; the steady-state
 *   context once navigation has fully settled.
 *
 * The navigation override is cleared only after the navigation terminates
 * (`NavigationEnd`, `NavigationCancel`, `NavigationError`) — never on
 * `ResolveEnd`, because the activation phase that follows is exactly when new
 * components fire their first queries.
 *
 * URLs matching any prefix from `AppContextRoutesConfig.skipUrlPrefixes` are
 * skipped entirely — the context for those routes is expected to be set
 * manually via a route-local guard or resolver calling
 * `AppContextService.setCurrentContext`.
 *
 * URLs starting with `AppContextRoutesConfig.supplyProjectPrefix` carry the
 * short project id in the segment right after that prefix, which takes
 * precedence over the `?projectId=` query fallback; when the prefix is not
 * configured, or the segment is not a valid short UUID, no project context is
 * derived from it.
 *
 * That fallback to `Global` is deliberate but invisible — the screen then loads
 * org-wide instead of project-scoped — so a URL under the Supply project prefix
 * that derives `Global` emits one `app_context.project_unresolved` warning per
 * navigation, carrying the route alone.
 */
@Injectable({ providedIn: 'root' })
export class AppContextListenerService implements OnDestroy {
	private readonly router = inject(Router);
	private readonly contextStore = inject(AppContextStoreService);
	private readonly permissionsService = inject(PermissionsService);
	private readonly routesConfig = inject(APP_CONTEXT_ROUTES);
	/** Optional: this library is `providedIn: 'root'` in applications that do not provide the logger. */
	private readonly logger = inject(ClientLoggerFacadeService, { optional: true });
	private readonly translator = short();
	private readonly destroy$ = new Subject<void>();

	constructor() {
		this.initRouterListener();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initRouterListener(): void {
		this.router.events
			.pipe(
				filter(
					(event): event is RouterLifecycleEvent =>
						event instanceof NavigationStart ||
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationError,
				),
				takeUntil(this.destroy$),
			)
			.subscribe(event => {
				const url = event instanceof NavigationEnd ? event.urlAfterRedirects : event.url;

				if (this.isSkippedUrl(url)) return;

				if (event instanceof NavigationStart) {
					const context = this.getAppContextFromUrl(event.url);
					this.contextStore.setNavigationContext(context);
					this.permissionsService.setModeFromContext(context);
				} else if (event instanceof NavigationEnd) {
					const context = this.getAppContextFromUrl(event.urlAfterRedirects);
					this.contextStore.setCurrentContext(context);
					this.contextStore.setNavigationContext(null);
					this.reportUnresolvedSupplyProject(context, url);
				} else {
					this.contextStore.setNavigationContext(null);
				}
			});
	}

	private isSkippedUrl(url: string): boolean {
		return this.routesConfig.skipUrlPrefixes?.some(prefix => url.startsWith(prefix)) ?? false;
	}

	/**
	 * Reports a URL under the Supply project prefix that derived `Global` — the
	 * screen loads org-wide where the reader expected one project, and nothing
	 * else says so.
	 *
	 * Both halves of the condition are required. A `Global` context on its own is
	 * the ordinary answer for every settings, admin and dashboard route, so
	 * reporting on it alone would ship one `warn` per navigation from every
	 * signed-in session. And an unconfigured `supplyProjectPrefix` is not a
	 * narrower prefix: it is the only field that names the Supply section, so
	 * without it no URL can be recognised as a Supply one and none is reported.
	 *
	 * **One record per navigation comes from the call site, not from a guard here.**
	 * This runs only on `NavigationEnd`, which fires once per completed navigation,
	 * so there is nothing to de-duplicate.
	 *
	 * **The metadata key is `unresolvedRoute`, not `route`, and the name is
	 * load-bearing.** `ClientLogEventEnricherService.enrich` stamps
	 * `route: location.pathname` over the caller's metadata unconditionally
	 * (`libs/client/core/logger/src/lib/pipeline/client-log-event-enricher.service.ts`),
	 * so a key called `route` is silently replaced before the record reaches any
	 * sink — and replaced with the WRONG value, since Angular's default
	 * `urlUpdateStrategy: 'deferred'` leaves the address bar on the previous URL
	 * until the navigation completes. Renaming the key is what makes the stripping
	 * above reach the SIEM at all.
	 */
	private reportUnresolvedSupplyProject(context: AppContext, url: string): void {
		const supplyPrefix = this.routesConfig.supplyProjectPrefix;

		if (!supplyPrefix || context.type !== AppContextType.Global) {
			return;
		}

		const route = toRoute(url);

		if (route !== supplyPrefix && !route.startsWith(`${supplyPrefix}/`)) {
			return;
		}

		this.logger?.warn(PROJECT_UNRESOLVED_EVENT, PROJECT_UNRESOLVED_TEXT, { unresolvedRoute: route });
	}

	private getAppContextFromUrl(url: string): AppContext {
		const cleanUrl = url.replace(/^\/(%23|#)/, '');

		const projectId = this.getProjectId(url);

		if (projectId) {
			return { type: AppContextType.Project, contextObjectId: projectId };
		}

		if (cleanUrl.startsWith(this.routesConfig.projectsPrefix) || cleanUrl === '/') {
			return { type: AppContextType.ProjectList };
		}

		return { type: AppContextType.Global };
	}

	private getProjectId(url: string): string | undefined {
		let projectId: string | undefined;

		const isScopeProjectUrl =
			url.startsWith(this.routesConfig.scopePrefix) && !url.startsWith(this.routesConfig.scopeLoginPrefix);

		if (url.startsWith(`${this.routesConfig.projectsPrefix}/`) || isScopeProjectUrl) {
			const pathParts = url.split('/');
			if (pathParts.length > 2 && pathParts[2] && !pathParts[2].includes('(')) {
				projectId = pathParts[2];
			}
		}

		projectId ??= this.getSupplyProjectShortId(url);

		if (!projectId) {
			projectId = this.extractProjectIdFromQueryParams(url);
		}

		return projectId && isValidShortUuid(projectId) ? this.translator.toUUID(projectId) : undefined;
	}

	/**
	 * Reads the short project id from the path segment right after
	 * `supplyProjectPrefix`. Returns `undefined` when the prefix is not
	 * configured, does not match, is followed by an empty segment, or carries a
	 * segment that is not a valid short UUID.
	 *
	 * A URL is external input: `short-uuid`'s `toUUID` throws on any character
	 * outside its alphabet, and that throw would happen inside the `router.events`
	 * subscriber — RxJS would swallow it, neither context setter would run, and the
	 * PREVIOUS project's context would stay authoritative for the new screen. A
	 * segment that fails validation therefore yields no project id at all, so the
	 * URL derives `Global` rather than the project that was open before it.
	 */
	private getSupplyProjectShortId(url: string): string | undefined {
		const supplyPrefix = this.routesConfig.supplyProjectPrefix;

		if (!supplyPrefix) {
			return undefined;
		}

		const projectPathPrefix = `${supplyPrefix}/`;

		if (!url.startsWith(projectPathPrefix)) {
			return undefined;
		}

		const [shortId] = url.slice(projectPathPrefix.length).split(SUPPLY_PROJECT_ID_TERMINATORS);

		return isValidShortUuid(shortId) ? shortId : undefined;
	}

	private extractProjectIdFromQueryParams(url: string): string | undefined {
		if (url.includes(`/${this.routesConfig.acceptInvitePath}`)) {
			return undefined;
		}
		const regexp = /[?&]projectId=([^&]+)/;
		const match = url.match(regexp);
		return match ? match[1] : undefined;
	}
}
